import { useState } from "react";
import { useOutletContext } from "react-router-dom";

const BASE_URL = "https://modem.slifetracker.com";

/* ── Helpers ──────────────────────────────────────────────── */
const MethodBadge = ({ method }) => {
    const colors = {
        GET:    { bg: '#e3f2fd', color: '#1255a1', border: '#90caf9' },
        POST:   { bg: '#e8f5e9', color: '#1b6e2c', border: '#a5d6a7' },
        PUT:    { bg: '#fff3e0', color: '#c75000', border: '#ffcc80' },
        DELETE: { bg: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
    };
    const s = colors[method] || colors.GET;
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 9px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 700,
            fontFamily: 'monospace',
            background: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
            marginRight: '8px',
            minWidth: '46px',
            textAlign: 'center',
        }}>
            {method}
        </span>
    );
};

const AuthBadge = ({ type }) => {
    const styles = {
        'API-KEY':   { bg: '#fce4ec', color: '#880e4f', label: '🔑 API-KEY' },
        'Token':     { bg: '#e8eaf6', color: '#283593', label: '🎫 Token' },
        'Public':    { bg: '#f3e5f5', color: '#4a148c', label: '🌐 Public' },
        'API-KEY + IP': { bg: '#fff8e1', color: '#e65100', label: '🔑 API-KEY + IP' },
    };
    const s = styles[type] || styles.Public;
    return (
        <span style={{
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '20px',
            background: s.bg,
            color: s.color,
            marginLeft: '6px',
        }}>
            {s.label}
        </span>
    );
};

const CodeBlock = ({ children }) => (
    <pre style={{
        background: '#1e2a3a',
        color: '#e2e8f0',
        borderRadius: '8px',
        padding: '14px 16px',
        fontSize: '12px',
        lineHeight: 1.6,
        overflowX: 'auto',
        margin: '8px 0 0',
        fontFamily: "'Courier New', Consolas, monospace",
    }}>
        {children}
    </pre>
);

const ParamRow = ({ name, type, required, desc }) => (
    <tr>
        <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#0d6efd', whiteSpace: 'nowrap' }}>
            {name}
            {required && <span style={{ color: '#dc3545', marginLeft: '3px' }}>*</span>}
        </td>
        <td style={{ fontSize: '11px', color: '#6c757d', fontFamily: 'monospace' }}>{type}</td>
        <td style={{ fontSize: '12px', color: '#495057' }}>{desc}</td>
    </tr>
);

const ParamTable = ({ params }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
        <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                <th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6c757d', padding: '6px 8px', textAlign: 'left', minWidth: '140px' }}>Paramètre</th>
                <th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6c757d', padding: '6px 8px', textAlign: 'left' }}>Type</th>
                <th style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6c757d', padding: '6px 8px', textAlign: 'left' }}>Description</th>
            </tr>
        </thead>
        <tbody>
            {params.map((p, i) => <ParamRow key={i} {...p} />)}
        </tbody>
    </table>
);

const Endpoint = ({ method, path, auth, summary, params, response, notes, example }) => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            marginBottom: '8px',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: open ? '#f4f7ff' : '#fff',
                    transition: 'background 0.12s',
                    gap: '4px',
                    flexWrap: 'wrap',
                }}
            >
                <MethodBadge method={method} />
                <code style={{ fontSize: '13px', color: '#1e3a5f', fontWeight: 500, flex: 1 }}>{BASE_URL}{path}</code>
                <AuthBadge type={auth} />
                <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '12px', display: open ? 'none' : 'inline' }}>
                    {summary}
                </span>
                <span style={{ marginLeft: 'auto', color: '#adb5bd', fontSize: '14px' }}>{open ? '▲' : '▼'}</span>
            </div>

            {/* Body */}
            {open && (
                <div style={{ padding: '16px 20px', borderTop: '1px solid #dee2e6', background: '#fafbfc' }}>
                    <p style={{ fontSize: '13px', color: '#495057', marginBottom: notes || params ? '14px' : 0 }}>{summary}</p>

                    {notes && (
                        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', fontSize: '12px', color: '#664d03', marginBottom: '14px' }}>
                            ⚠️ {notes}
                        </div>
                    )}

                    {params && params.length > 0 && (
                        <div style={{ marginBottom: '14px' }}>
                            <p className="section-title" style={{ fontSize: '11px' }}>Paramètres</p>
                            <ParamTable params={params} />
                            <p style={{ fontSize: '10px', color: '#adb5bd', margin: '4px 0 0' }}>* requis</p>
                        </div>
                    )}

                    {example && (
                        <div style={{ marginBottom: '14px' }}>
                            <p className="section-title" style={{ fontSize: '11px' }}>Exemple de requête</p>
                            <CodeBlock>{example}</CodeBlock>
                        </div>
                    )}

                    {response && (
                        <div>
                            <p className="section-title" style={{ fontSize: '11px' }}>Exemple de réponse</p>
                            <CodeBlock>{response}</CodeBlock>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Section = ({ icon, title, color, description, children }) => (
    <div style={{ marginBottom: '32px' }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 20px',
            background: color,
            borderRadius: '8px 8px 0 0',
            borderBottom: '2px solid rgba(0,0,0,0.08)',
        }}>
            <span style={{ fontSize: '20px' }}>{icon}</span>
            <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#fff' }}>{title}</div>
                {description && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>{description}</div>}
            </div>
        </div>
        <div style={{ border: '1px solid #dee2e6', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', background: '#fff' }}>
            {children}
        </div>
    </div>
);

/* ── Page principale ──────────────────────────────────────── */
const Api = () => {
    const [token] = useOutletContext();

    return (
        <div style={{ maxWidth: '1000px' }}>
            {/* Hero */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #0d6efd 100%)',
                borderRadius: '12px',
                padding: '28px 32px',
                marginBottom: '28px',
                color: '#fff',
            }}>
                <h4 style={{ fontWeight: 700, margin: '0 0 6px' }}>⚡ Documentation API — SLife Gateway</h4>
                <p style={{ margin: '0 0 14px', opacity: 0.75, fontSize: '14px' }}>
                    Passerelle de paiement Mobile Money Madagascar — Telma/mVola · Orange Money · Airtel Money
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.15)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px' }}>
                        🌐 Base URL : <code style={{ color: '#90caf9' }}>{BASE_URL}</code>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.15)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px' }}>
                        📦 Format : <code style={{ color: '#90caf9' }}>application/json</code>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.15)', padding: '6px 14px', borderRadius: '6px', fontSize: '12px' }}>
                        🎫 Token admin actif : <code style={{ color: '#a5d6a7' }}>{token?.substring(0, 8)}…</code>
                    </div>
                </div>
            </div>

            {/* Auth info */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                {[
                    { label: '🌐 Public', desc: 'Aucune authentification', bg: '#f3e5f5', col: '#4a148c' },
                    { label: '🔑 API-KEY', desc: 'Header: API-KEY: <clé>', bg: '#fce4ec', col: '#880e4f' },
                    { label: '🔑 API-KEY + IP', desc: 'API-KEY + IP dans whitelist', bg: '#fff8e1', col: '#e65100' },
                    { label: '🎫 Token', desc: 'URL: /admin/:token/...', bg: '#e8eaf6', col: '#283593' },
                ].map(a => (
                    <div key={a.label} style={{ background: a.bg, padding: '10px 16px', borderRadius: '8px', flex: 1, minWidth: '180px' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: a.col }}>{a.label}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{a.desc}</div>
                    </div>
                ))}
            </div>

            {/* ── Section Marchand ────────────────────────── */}
            <Section icon="🏪" title="API Marchand" color="#1b5e20"
                description="Endpoints pour créer et suivre des transactions de paiement">

                <Endpoint
                    method="POST" path="/api/trx/intentTrx" auth="API-KEY + IP"
                    summary="Créer une intention de transaction — Point d'entrée principal pour initier un paiement."
                    notes="La signature hash est obligatoire : MD5(`{orderId}:{passerelle}:{amount}:{phoneNumber}:{type}:{uriWebhook}:{uriSuccess}:{uriCanceled}:{MD5(secret_key)}`)"
                    params={[
                        { name: 'type', type: 'string', required: true, desc: '"retrait" ou "depot"' },
                        { name: 'amount', type: 'number', required: true, desc: 'Montant en Ariary (>= 1)' },
                        { name: 'orderId', type: 'string', required: true, desc: 'Identifiant unique côté marchand' },
                        { name: 'phoneNumber', type: 'string', required: true, desc: 'Numéro Mobile Money du client (034, 032, 033...)' },
                        { name: 'description', type: 'string', required: true, desc: 'Description affichée au client' },
                        { name: 'passerelle', type: 'string[]', required: true, desc: 'Liste d\'IMEI des passerelles autorisées' },
                        { name: 'uriWebhook', type: 'string', required: true, desc: 'URL callback de confirmation (POST)' },
                        { name: 'uriSuccess', type: 'string', required: true, desc: 'URL de redirection après succès' },
                        { name: 'uriCanceled', type: 'string', required: true, desc: 'URL de redirection après annulation' },
                        { name: 'hash', type: 'string', required: true, desc: 'Signature MD5 de la requête' },
                        { name: 'instruction', type: 'string', required: false, desc: 'Instruction affichée au client (ex: code PIN)' },
                        { name: 'instructionisCallAction', type: 'boolean', required: false, desc: 'Transforme le bouton Copier de l\'instruction en bouton d\'appel' },
                        { name: 'instruction2', type: 'string', required: false, desc: 'Instruction secondaire' },
                        { name: 'uriLogo', type: 'string', required: false, desc: 'URL du logo à afficher sur la page de paiement' },
                        { name: 'uriWebhookUssd', type: 'string', required: false, desc: 'URL callback USSD séparé' },
                        { name: 'meta_data', type: 'array', required: false, desc: 'Données supplémentaires [{key, value}]' },
                        { name: 'cancelPending', type: 'boolean', required: false, desc: 'Annule une session pending identique de la meme API avant de creer la nouvelle intention' },
                    ]}
                    example={`POST /api/trx/intentTrx
Content-Type: application/json
API-KEY: votre_api_key

{
  "type": "depot",
  "amount": 10000,
  "orderId": "CMD-2024-001",
  "phoneNumber": "0341234567",
  "description": "Paiement commande #001",
  "passerelle": ["356938035643809"],
  "uriWebhook": "https://monsite.mg/webhook",
  "uriSuccess": "https://monsite.mg/success",
  "uriCanceled": "https://monsite.mg/cancel",
  "cancelPending": true,
  "instruction": "#111*1*1*0341234567*10000#",
  "instructionisCallAction": true,
  "hash": "a1b2c3d4e5f6..."
}`}
                    response={`{
  "URI": "https://gateway.slifetracker.com?public=123456789"
}`}
                />

                <Endpoint
                    method="POST" path="/api/trx/cancelPending" auth="API-KEY + IP"
                    summary="Annuler une transaction pending identique pour liberer le numero et le modem."
                    params={[
                        { name: 'type', type: 'string', required: true, desc: '"retrait" ou "depot"' },
                        { name: 'amount', type: 'number', required: true, desc: 'Montant de la transaction pending' },
                        { name: 'phoneNumber', type: 'string', required: true, desc: 'Numero Mobile Money utilise dans la session pending' },
                        { name: 'passerelle', type: 'string[]', required: true, desc: 'Tableau avec un seul IMEI, identique a la session pending' },
                    ]}
                    example={`POST /api/trx/cancelPending
Content-Type: application/json
API-KEY: votre_api_key

{
  "type": "depot",
  "amount": 10000,
  "phoneNumber": "0341234567",
  "passerelle": ["356938035643809"]
}`}
                    response={`{
  "request": "OK",
  "trxId": "65..."
}`}
                />

                <Endpoint
                    method="GET" path="/api/trx/infoTrx/:orderId" auth="API-KEY + IP"
                    summary="Récupérer les informations d'une transaction par son orderId marchand."
                    params={[
                        { name: ':orderId', type: 'string', required: true, desc: 'Identifiant de commande (URL param)' },
                    ]}
                    response={`{
  "public_id": "123456789",
  "orderId": "CMD-2024-001",
  "type": "depot",
  "amount": 10000,
  "status": "completed",
  "phoneNumber": "0341234567",
  "description": "Paiement commande #001",
  "expiredAt": "2024-01-15T11:30:00Z",
  "meta_data": []
}`}
                />

                <Endpoint
                    method="GET" path="/api/checkPasserelle/:imei" auth="API-KEY + IP"
                    summary="Vérifier l'état et la configuration d'une passerelle GSM par son IMEI."
                    params={[
                        { name: ':imei', type: 'string', required: true, desc: 'IMEI du modem GSM (URL param)' },
                    ]}
                    response={`{
  "imei": "356938035643809",
  "name": "Passerelle A",
  "isConnected": true,
  "isUP": true,
  "canRetrait": true,
  "canUssd": true,
  "isTpe": false,
  "operateur": [
    { "name": "Compte 1", "phone": "0341234567", "operateur": "telma" }
  ]
}`}
                />

                <Endpoint
                    method="POST" path="/api/generateToken" auth="API-KEY + IP"
                    summary="Générer un token d'authentification admin (valide ~83 minutes)."
                    params={[
                        { name: 'userId', type: 'number', required: true, desc: 'Identifiant utilisateur' },
                        { name: 'apiId', type: 'string', required: true, desc: 'ObjectId de l\'API' },
                    ]}
                    response={`{
  "token": "f47ac10b-58cc-4372-a567-0e02b2c3d479"
}`}
                />
            </Section>

            {/* ── Section Admin ────────────────────────────── */}
            <Section icon="🛡️" title="API Admin" color="#1e3a5f"
                description={`Endpoints de gestion — Token passé en URL : /admin/${token?.substring(0, 8)}…/`}>

                <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '16px' }}>
                    Toutes les routes admin sont préfixées par <code>/admin/:token/</code> où <code>:token</code> est votre token d'accès.
                </p>

                {/* Transactions */}
                <p className="section-title">Transactions</p>

                <Endpoint
                    method="POST" path="/admin/:token/trx/list" auth="Token"
                    summary="Lister les transactions avec pagination et filtres."
                    params={[
                        { name: 'filtre.page', type: 'number', required: false, desc: 'Page courante (défaut: 1)' },
                        { name: 'filtre.perpage', type: 'number', required: false, desc: 'Résultats par page (défaut: 10)' },
                        { name: 'filtre.key', type: 'array', required: false, desc: '[{title, value}] — filtre sur champ exact' },
                        { name: 'filtre.limiter', type: 'array', required: false, desc: '[{title, min, max}] — filtre par plage de date' },
                        { name: 'filtre.sort', type: 'array', required: false, desc: '[{title, value}] — tri (-1 DESC, 1 ASC)' },
                        { name: 'filtre.metaFilters', type: 'array', required: false, desc: '[{key, value}] — filtre dans meta_data' },
                    ]}
                    response={`{
  "data": [
    {
      "_id": "...",
      "public_id": "123456789",
      "orderId": "CMD-001",
      "type": "depot",
      "amount": 10000,
      "status": "completed",
      "createdAt": "2024-01-15T10:00:00Z",
      "apiName": "Mon API",
      "txId": "REF123",
      "meta_data": [],
      "passerelle": [{ "name": "Pass A" }],
      "_ussd": { "message": "...", "isPending": false }
    }
  ],
  "filter": { "page": 1, "pages": 5, "perpage": 10, ... }
}`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/trx/confirm" auth="Token"
                    summary="Confirmer une transaction ou ajouter une référence externe."
                    params={[
                        { name: 'trxId', type: 'string', required: true, desc: 'ObjectId de la transaction' },
                        { name: 'ref', type: 'string', required: true, desc: 'Référence externe (n° transaction opérateur)' },
                        { name: 'type', type: 'string', required: true, desc: '"confirm" (valide) | "addRef" (ajoute réf) | "cancel" (annule)' },
                    ]}
                    response={`{ "request": "OK" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/trx/cancel" auth="Token"
                    summary="Annuler une transaction en cours. Arrête l'USSD associé et appelle le webhook."
                    params={[
                        { name: 'trxId', type: 'string', required: true, desc: 'ObjectId de la transaction' },
                    ]}
                    response={`{ "request": "OK" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/trx/reactive" auth="Token"
                    summary="Réactiver une transaction expirée — repasse le statut à 'checking'."
                    params={[
                        { name: 'trxId', type: 'string', required: true, desc: 'ObjectId de la transaction expirée' },
                    ]}
                    response={`{ "request": "OK" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/trx/reactiveUssd" auth="Token"
                    summary="Relancer la commande USSD associée à une transaction (re-déclenche l'automatisation)."
                    params={[
                        { name: 'trxId', type: 'string', required: true, desc: 'ObjectId de la transaction' },
                    ]}
                    response={`{ "request": "OK" }`}
                />

                {/* SMS */}
                <p className="section-title" style={{ marginTop: '20px' }}>SMS Reçus</p>

                <Endpoint
                    method="POST" path="/admin/:token/smsReceive/list" auth="Token"
                    summary="Lister les SMS reçus par les modems avec pagination."
                    params={[
                        { name: 'filtre.page', type: 'number', required: false, desc: 'Page (défaut: 1)' },
                        { name: 'filtre.perpage', type: 'number', required: false, desc: 'Par page (défaut: 10)' },
                        { name: 'filtre.limiter', type: 'array', required: false, desc: '[{title: "createdAt", min, max}]' },
                    ]}
                    response={`{
  "data": [
    {
      "_id": "...",
      "phone": "MVola",
      "message": "Vous avez reçu 10000 Ar de 034XXXXXXX...",
      "createdAt": "2024-01-15T10:05:00Z",
      "passerelle": { "name": "Pass A" }
    }
  ],
  "filter": { "page": 1, "pages": 3 }
}`}
                />

                {/* Passerelles */}
                <p className="section-title" style={{ marginTop: '20px' }}>Passerelles</p>

                <Endpoint
                    method="GET" path="/admin/:token/myPasserelle" auth="Token"
                    summary="Récupérer la liste de toutes les passerelles GSM associées à ce compte."
                    response={`[
  {
    "_id": "...",
    "imei": "356938035643809",
    "name": "Passerelle A",
    "isConnected": true,
    "canRetrait": true,
    "canUssd": true,
    "isTpe": false,
    "solde": { "telma": 50000, "orange": 30000, "airtel": 0 },
    "operateur": [
      { "name": "Compte mVola", "phone": "0341234567", "operateur": "telma" }
    ],
    "proprietaire": { "name": "Mon Org", "contact": "0341234567" }
  }
]`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/myPasserelle/Update" auth="Token"
                    summary="Mettre à jour la configuration d'une passerelle (nom, opérateurs, options, etc.)."
                    params={[
                        { name: 'data.id', type: 'string', required: true, desc: 'ObjectId de la passerelle' },
                        { name: 'data.name', type: 'string', required: false, desc: 'Nom de la passerelle' },
                        { name: 'data.imei', type: 'string', required: false, desc: 'IMEI du modem' },
                        { name: 'data.canRetrait', type: 'boolean', required: false, desc: 'Autorise les retraits' },
                        { name: 'data.canUssd', type: 'boolean', required: false, desc: 'Autorise l\'USSD automatique' },
                        { name: 'data.isTpe', type: 'boolean', required: false, desc: 'Mode TPE activé' },
                        { name: 'data.isTpeRetrait', type: 'boolean', required: false, desc: 'TPE retrait activé' },
                        { name: 'data.isCashPoint', type: 'boolean', required: false, desc: 'Mode Cash Point' },
                        { name: 'data.operateur', type: 'array', required: false, desc: '[{name, operateur, phone}] — max 2 entrées' },
                        { name: 'data.proprietaire', type: 'object', required: false, desc: '{name, adresse, contact, nameResponsable}' },
                        { name: 'data.code_merchant', type: 'string', required: false, desc: 'Code marchand Orange' },
                        { name: 'data.number_merchant', type: 'string', required: false, desc: 'Numéro pour transactions marchandes' },
                    ]}
                    response={`{ "message": "Passerelle mis à jour" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/myPasserelle/SendUssd" auth="Token"
                    summary="Envoyer une commande USSD personnalisée via le modem GSM."
                    params={[
                        { name: 'data.imei', type: 'string', required: true, desc: 'IMEI du modem cible' },
                        { name: 'data.ussd', type: 'string', required: true, desc: 'Code USSD (ex: #111*1*6*1#)' },
                        { name: 'data.code', type: 'string', required: false, desc: 'Code de réponse USSD (pour menus multi-niveaux)' },
                    ]}
                    response={`{ "message": "Ussd envoyé" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/myPasserelle/SendRetrait" auth="Token"
                    summary="Déclencher une transaction entre comptes Mobile Money."
                    params={[
                        { name: 'data.imei', type: 'string', required: true, desc: 'IMEI du modem' },
                        { name: 'data.retrait_type', type: 'string', required: true, desc: '"to_merchant" | "merchant_to_merchant" | "merchant_to_orangemoney" | "orangemoney_to_orangemoney" | "to_mvola"' },
                        { name: 'data.retrait_amount', type: 'number', required: true, desc: 'Montant en Ariary' },
                    ]}
                    response={`{ "message": "Transaction en cours" }`}
                />

                <Endpoint
                    method="POST" path="/admin/:token/myPasserelle/Restart" auth="Token"
                    summary="Planifier le redémarrage d'un modem GSM (ne redémarre pas si un reboot est déjà en attente depuis moins de 15 min)."
                    params={[
                        { name: 'data.imei', type: 'string', required: true, desc: 'IMEI du modem à redémarrer' },
                    ]}
                    response={`{ "message": "Redémarage en cours" }
// ou
{ "message": "un redémarage est déja en attente" }`}
                />
            </Section>

            {/* ── Section Publique ─────────────────────────── */}
            <Section icon="🌐" title="API Publique" color="#00695c"
                description="Endpoints accessibles sans authentification (client final, santé)">

                <Endpoint
                    method="GET" path="/ws/CheckTrx/:public_id" auth="Public"
                    summary="Vérifier le statut d'une transaction (polling par la page de paiement client, toutes les 10s)."
                    params={[
                        { name: ':public_id', type: 'string', required: true, desc: 'Identifiant public de la transaction (8-9 chiffres)' },
                    ]}
                    response={`{
  "status": "pending",
  "type": "depot",
  "amount": 10000,
  "expiredAt": "11:30:00",
  "uriSuccess": "https://...",
  "uriCanceled": "https://...",
  "messageIncident": "0341234567",
  "passerelle": [
    {
      "name": "Pass A",
      "operateur": [{ "phone": "0341234567", "name": "mVola", "operateur": "telma" }],
      "tpe": false,
      "auto": true
    }
  ],
  "meta_data": [
    { "key": "uriLogo", "value": "https://..." },
    { "key": "phoneNumber", "value": "0341234567" },
    { "key": "instruction", "value": "#111*1*1*0341234567*10000#" },
    { "key": "instructionisCallAction", "value": "true" }
  ]
}`}
                />

                <Endpoint
                    method="GET" path="/apiTest" auth="Public"
                    summary="Test de santé du serveur."
                    response={`{ "request": "ok" }`}
                />
            </Section>

            {/* ── Erreurs standard ─────────────────────────── */}
            <div className="admin-card" style={{ padding: '20px', marginBottom: '32px' }}>
                <p className="section-title">Format d'erreur standard</p>
                <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                    Toutes les erreurs retournent un objet JSON avec les codes HTTP suivants :
                    <strong> 401</strong> (non authentifié), <strong>404</strong> (non trouvé / validation), <strong>500</strong> (erreur serveur).
                </p>
                <CodeBlock>{`{
  "status": "erreur",
  "message": "Description de l'erreur",
  "data": [
    { "message": "Détail du champ invalide" }
  ]
}`}</CodeBlock>
            </div>
        </div>
    );
};

export default Api;
